import { View, Text } from 'react-native'
import { router, useLocalSearchParams } from 'expo-router'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/src/common/components/Button'
import { Check } from '@/src/assets/icons/Check'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useLogError } from '@/src/common/hooks/useLogError'
import { z } from 'zod/v4'
import { triggerSegmentAction } from '@/src/services/segment'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { Routes } from '../navigation/routes'
import { useCardBenefitsParameterStore } from '../statsig/store/useCardBenefitsParameterStore'
import { CardBenefitsComponents as Components } from '../components'
import { RouteInterface } from '@/src/common/enums/routes'
import BottomSheet from '@gorhom/bottom-sheet'
import {
  BenefitModal,
  useIsBenefitScreenPresentedAsModal,
} from '@/src/experiences/card-benefits/components/BenefitModal'
import { vibrateSuccess } from '@/src/common/utils/vibrate'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'
import { INSTACART_PARTNER_NAME, REFERRAL_BONUS_OFFER_PARTNER_NAME } from '../constants'
import { useGetCardAccount } from '../hooks/useGetCardAccount'
import { useBenefitsEnrollments } from '../hooks/useBenefitsEnrollments'
import { useBenefits } from '../hooks/useBenefits'
import { useBenefitEnroll } from '../hooks/useBenefitEnroll'

const params = z.object({ partnerName: z.string() })

export function Benefit() {
  const parseParamsResult = params.safeParse(useLocalSearchParams())
  const partnerName = parseParamsResult.success ? parseParamsResult.data.partnerName : ''
  const Alert = useAlert(Benefit.name)
  const onError = useLogError()

  const cardBenefitsStore = useCardBenefitsParameterStore()
  const enrollmentModalRef = useRef<BottomSheet>(null)
  const nonCardModalRef = useRef<BottomSheet>(null)
  const [benefitId, setBenefitId] = useState<string>()
  const [creditPerRefresh, setCreditPerRefresh] = useState<number | null>()
  const [refreshes, setRefreshes] = useState<string | null>()
  const [creditRedemption, setCreditRedemption] = useState<string>()

  const [enrollInBenefitMutation, enrollInBenefitResult] = useBenefitEnroll()

  const { data: benefitsData } = useBenefits()
  const benefit = benefitsData?.find((benefit) => benefit.partnerName === partnerName)

  useEffect(() => {
    if (benefit) {
      setBenefitId(benefit.id)
      setCreditPerRefresh(benefit.creditPerRefresh)
      setRefreshes(benefit.refreshes)
      setCreditRedemption(benefit.creditRedemption ?? undefined)
    }
  }, [benefit])

  const { data: benefitEnrollmentsData } = useBenefitsEnrollments()

  const isEnrolled =
    benefitEnrollmentsData?.find((benefitEnrollment) => benefitEnrollment.benefitId === benefitId)
      ?.isEnrolled || false

  const cardAccount = useGetCardAccount()
  const isNonCardMember = !cardAccount.data

  const isBenefitScreenPresentedAsModal = useIsBenefitScreenPresentedAsModal()

  // grab the instacart url if the user is enrolled, this will grab the original url
  // could potentiall drive this from the benefit enrollments query if we see follow up CTAs
  // in the future. Right now there is no communication between us and Instacart if the user
  // successfully enrolls. This is unique per user
  if (isEnrolled && partnerName === INSTACART_PARTNER_NAME) {
    router.replace(Routes.InstacartEnrollment.route)
    return null
  }

  if (partnerName === REFERRAL_BONUS_OFFER_PARTNER_NAME) {
    router.replace(Routes.ReferralBonusOffer.route)
    return null
  }

  if (!(partnerName && benefitId)) return

  const cardBenefitsCMS = cardBenefitsStore.get('benefits_v1')

  const selectedBenefit = cardBenefitsCMS?.[partnerName]

  if (!selectedBenefit) return

  // Redirect to the benefits list to prevent direct access if the benefit is hidden from the list.
  // Direct access by pasting the URL into the browser is prevented by the window.history.length check.
  if (
    selectedBenefit.should_hide_from_list &&
    !isEnrolled &&
    (!router.canGoBack() ||
      // window.history may be undefined for mobile
      (typeof window !== 'undefined' &&
        typeof window.history !== 'undefined' &&
        window.history.length <= 2))
  ) {
    router.replace(Routes.Benefits.route)
    return
  }

  const enrollInBenefit = async () => {
    if (isNonCardMember) {
      nonCardModalRef.current?.snapToIndex(0)
    } else if (!isEnrolled) {
      try {
        await enrollInBenefitMutation({ params: { benefitName: partnerName } })
        try {
          await vibrateSuccess()
        } finally {
          enrollmentModalRef.current?.snapToIndex(0)
        }
      } catch (error) {
        Alert.alert('Error', `We've encountered an error while trying to update enrollment status`)
        onError(error)
      }
    }
  }

  const ctaText = () => {
    // backwards compatibility, as cta_action was not added until 2025-02-07
    if (!selectedBenefit.cta_action) {
      return isEnrolled ? `You're Enrolled` : `Enroll in Benefit`
    }

    switch (selectedBenefit.cta_action) {
      case 'enroll':
        if (isEnrolled) {
          return selectedBenefit.enrolled_cta_action && selectedBenefit.enrolled_cta_action_text
            ? selectedBenefit.enrolled_cta_action_text
            : "You're Enrolled"
        }
        return 'Enroll in Benefit'

      case 'enroll_in_offer':
        return isEnrolled ? 'Enrolled in Offer' : 'Enroll in Offer'

      case 'enrollment_full':
        return isEnrolled ? 'Enrolled in Offer' : 'Offer No Longer Available'

      case 'launch_link':
        return 'Get Started Today'
      default:
        selectedBenefit.cta_action satisfies never
        return 'Get Started Today'
    }
  }

  const onPress = () => {
    if (selectedBenefit.navigate_to) {
      const cardBenefitsRouteNames = Object.values(Routes).map((routes) => routes.route)
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const cardBenefitNavigateToRoute = selectedBenefit.navigate_to
        .pathname as (typeof cardBenefitsRouteNames)[number]
      if (isNonCardMember) {
        nonCardModalRef.current?.snapToIndex(0)
      } else if (cardBenefitsRouteNames.includes(cardBenefitNavigateToRoute)) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        router.push(selectedBenefit.navigate_to as RouteInterface['route'])
      }
    } else if (selectedBenefit.cta_link) {
      if (isNonCardMember) {
        nonCardModalRef.current?.snapToIndex(0)
      } else {
        void openInAppBrowser(selectedBenefit.cta_link, selectedBenefit.header)
      }
    } else if (isEnrolled && selectedBenefit.enrolled_cta_action) {
      switch (selectedBenefit.enrolled_cta_action) {
        // Just a single case for now, so disable the type check
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        case 'launch_link':
          if (selectedBenefit.enrolled_cta_link) {
            void openInAppBrowser(selectedBenefit.enrolled_cta_link, selectedBenefit.header)
          }
          break
        default:
          selectedBenefit.enrolled_cta_action satisfies never
          break
      }
    } else {
      void enrollInBenefit()
    }
  }

  const hasCompletedAction = () => {
    if (selectedBenefit.enrolled_cta_action) {
      return false
    }
    // backwards compatibility, as cta_action was not added until 2025-02-07
    if (!selectedBenefit.cta_action) {
      return isEnrolled
    }

    const cta_action = selectedBenefit.cta_action
    switch (cta_action) {
      case 'enroll':
      case 'enroll_in_offer':
      case 'enrollment_full':
        return isEnrolled
      case 'launch_link':
        return false // no need to check for launch link
      default:
        cta_action satisfies never
        return true
    }
  }

  const enrollmentButtonDisabled =
    hasCompletedAction() ||
    enrollInBenefitResult.isLoading ||
    selectedBenefit.cta_action === 'enrollment_full' ||
    cardAccount.isUninitialized ||
    cardAccount.isLoading ||
    cardAccount.isError

  return (
    <BenefitModal screenName="benefit">
      <Components.Container>
        <Components.Header title={selectedBenefit.header} screenName="benefit" />
        <Components.Scroll>
          <Components.Hero
            source={{ uri: selectedBenefit.thumbnail_img }}
            backgroundColor={selectedBenefit.theme.backgroundColor}
          />
          <View
            style={[
              { padding: 16, backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
              !isBenefitScreenPresentedAsModal && {
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              },
            ]}
          >
            <Components.Title title={selectedBenefit.title} />
            <Components.RedemptionProgress
              isEnrolled={isEnrolled}
              creditPerRefresh={creditPerRefresh ?? null}
              benefitId={benefitId}
              refreshes={refreshes ?? null}
              creditRedemption={creditRedemption ?? null}
            />
            <Components.OfferIsNowFull ctaAction={selectedBenefit.cta_action ?? ''} />
            <Components.Summary
              summary={
                selectedBenefit.cta_action === 'enrollment_full' &&
                selectedBenefit.enrollment_full_summary
                  ? selectedBenefit.enrollment_full_summary
                  : selectedBenefit.summary
              }
            />
            <Components.HowToRedeem
              howToRedeem={selectedBenefit.how_to_redeem}
              creditRedemption={creditRedemption}
              ctaAction={selectedBenefit.cta_action}
            />

            <Button
              testID="enroll-in-benefit"
              containerStyle={{ marginTop: 24, paddingHorizontal: 16 }}
              disabled={enrollmentButtonDisabled}
              style={{
                backgroundColor: enrollmentButtonDisabled
                  ? mesaColorNames['Mesa - Brand/White #FFFFFF']
                  : mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
              }}
              {...(enrollmentButtonDisabled
                ? { borderColor: mesaColorNames['Acre Turquoise/500 #00DFC9'], borderWidth: 1 }
                : undefined)}
              onPress={() => {
                onPress()
                void triggerSegmentAction({
                  component: 'Button',
                  action: 'User Enrolled In Benefit',
                  benefitName: selectedBenefit.header,
                })
              }}
              loading={enrollInBenefitResult.isLoading}
            >
              <>
                {hasCompletedAction() && <Check height={20} width={20} />}
                <Text
                  style={[
                    {
                      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
                      fontWeight: 700,
                      fontSize: 16,
                      marginLeft: 8,
                    },
                    hasCompletedAction() && {
                      color: mesaColorNames['Acre Turquoise/800 #086762'],
                    },
                  ]}
                >
                  {ctaText()}
                </Text>
              </>
            </Button>
          </View>

          <Components.Terms terms={selectedBenefit.terms} />
        </Components.Scroll>

        <Components.EnrollmentSuccessModal
          selectedBenefit={selectedBenefit}
          bottomSheetRef={enrollmentModalRef}
        />

        <Components.NonMesaMemberModal bottomSheetRef={nonCardModalRef} />
      </Components.Container>
    </BenefitModal>
  )
}
